import { useEffect, useState } from "react";

/**
 * Single mobile breakpoint for the whole app (Mobile DMS v1).
 *
 * 768px matches the one pre-existing media query in `LayoutStyled.js`
 * and antd's `md` breakpoint, so the JS branch (which nav/page shell
 * mounts) and the CSS branch (margins, pane widths) flip together.
 */
export const MOBILE_BREAKPOINT_PX = 768;

export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;

const getMatches = () => {
  // matchMedia is stubbed in tests (setupTests.js) and always present in
  // real browsers; the guards cover SSR-ish tooling (Storybook docs mode)
  // and test files that replace matchMedia with a bare jest.fn().
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return Boolean(window.matchMedia(MOBILE_MEDIA_QUERY)?.matches);
};

/**
 * True when the viewport is phone-sized. Subscribes to the media query
 * so rotation / window resize flips the layout live without a reload.
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    if (!mql) return undefined;
    const onChange = (event) => setIsMobile(event.matches);
    // Sync once on mount in case the viewport changed between the lazy
    // initial state read and the effect running.
    setIsMobile(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari < 14 fallback (matches the stub shape in setupTests.js).
    if (mql.addListener) {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  return isMobile;
};

export default useIsMobile;
